import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { useLang } from "@/context/LangContext";
import { scrollToId } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

function ArchVisual({ base }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[440px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.6, ease: EASE, delay: base + 0.2 }}
          className="arch relative aspect-[3/4.3] overflow-hidden bg-[#141011]"
        >
          <motion.img
            src="/img/hero.webp"
            alt=""
            style={{ y: imgY }}
            initial={{ scale: 1.25 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: 2.4, ease: EASE, delay: base + 0.2 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,transparent_30%,rgba(11,11,12,0.75)_100%)]" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: base + 1 }}
          className="arch pointer-events-none absolute -inset-3 border border-[#C6A76A]/35 [transform:translateZ(40px)]"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: base + 1.3, ease: EASE }}
          className="absolute -bottom-6 start-1/2 flex h-24 w-24 -translate-x-1/2 items-center justify-center rounded-full border border-[#C6A76A]/40 bg-[#0B0B0C]/80 backdrop-blur-md [transform:translateZ(70px)] rtl:translate-x-1/2"
        >
          <motion.svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full text-[#C6A76A]" animate={{ rotate: 360 }} transition={{ duration: 28, repeat: Infinity, ease: "linear" }}>
            <defs>
              <path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
            </defs>
            <text fontSize="8.5" letterSpacing="3.2" fill="currentColor" fontFamily="Jost">
              <textPath href="#circ">ATOR RWIHA · HAUTE PARFUMERIE · </textPath>
            </text>
          </motion.svg>
          <span className="font-arabic-display text-2xl text-[#F7F3EB]">ر</span>
        </motion.div>
      </motion.div>
    </div>
  );
}

export function Hero({ introDelay = 0 }) {
  const { t, rtl } = useLang();
  const base = introDelay;
  return (
    <section id="top" data-testid="hero-section" className="grain relative isolate min-h-[100svh] overflow-hidden bg-[#0B0B0C] text-[#F7F3EB]">
      <div className="pointer-events-none absolute -top-40 start-[20%] h-[620px] w-[620px] rounded-full bg-[#C6A76A]/[0.07] blur-[120px]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B0B0C] to-transparent" />
      <div className="relative mx-auto grid min-h-[100svh] max-w-[1440px] grid-cols-1 items-center gap-14 px-5 pb-24 pt-32 sm:px-10 lg:grid-cols-12 lg:gap-8 lg:pb-16">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: base }} className="flex items-center gap-4">
            <span className="h-px w-12 bg-[#C6A76A]" />
            <span className="eyebrow">{t("hero_over")}</span>
          </motion.div>
          <h1 data-testid="hero-title" className={`font-display mt-8 text-[#F7F3EB] ${rtl ? "text-[3.4rem] leading-[1.35] sm:text-7xl lg:text-[6.4rem]" : "text-[3.6rem] leading-[0.98] sm:text-7xl lg:text-[7.5rem]"}`}>
            <MaskLine delay={base + 0.15}>{t("hero_l1")}</MaskLine>
            <MaskLine delay={base + 0.3} className="ps-[8%]">{t("hero_l2")}</MaskLine>
            <MaskLine delay={base + 0.45} className={`text-[#C6A76A] ${rtl ? "text-[0.6em]" : "italic"}`}>
              {t("hero_l3")}
            </MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: base + 0.8, ease: EASE }} className="mt-8 max-w-md text-base leading-relaxed text-[#F7F3EB]/75 sm:text-lg">
            {t("hero_sub")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: base + 0.95, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-6">
            <button
              data-testid="hero-explore-collection-btn"
              onClick={() => scrollToId("collection")}
              className="btn-gold border border-[#C6A76A] px-8 py-4 text-[0.78rem] tracking-[0.25em] text-[#F7F3EB] transition-colors duration-500 hover:text-[#0B0B0C] rtl:text-base rtl:tracking-normal"
            >
              {t("cta_explore")}
            </button>
            <button data-testid="hero-story-btn" onClick={() => scrollToId("house")} className="link-line pb-1 text-[0.78rem] tracking-[0.25em] text-[#F7F3EB]/80 hover:text-[#F7F3EB] rtl:text-base rtl:tracking-normal">
              {t("cta_house")}
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <ArchVisual base={base} />
        </div>
      </div>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: base + 1.6 }}
        onClick={() => scrollToId("house")}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 start-5 hidden items-center gap-3 text-[0.7rem] tracking-[0.3em] text-[#F7F3EB]/70 sm:start-10 lg:flex"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2.2, repeat: Infinity }}>
          <ArrowDown className="h-4 w-4 text-[#C6A76A]" strokeWidth={1.2} />
        </motion.span>
        {t("scroll")}
      </motion.button>
    </section>
  );
}
